import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { ISiteFooter, ISiteHeader } from 'declarations/contentful';
import { useRouter } from 'next/router';

import MarketplaceMapLayout from '../../properties/shared/MarketplaceMapLayout';
import { ListingCard } from '../property/SimilarListings';
import type { ListingCardData } from '../property/SimilarListings';
import { CityLinkList } from './CityLinkList';
import type { CityLink } from './CityLinkList';
import { DirectoryHeader } from './DirectoryHeader';
import { DirectorySeoHead } from './DirectorySeoHead';
import { StateLinkList } from './StateLinkList';
import type { StateLink } from './StateLinkList';

const PAGE_SIZE = 30;

interface DirectoryTemplateProps {
  aiBlurb?: string | null;
  canonicalUrl: string;
  citiesWithInventory: CityLink[];
  currentPage: number;
  footer?: ISiteFooter | null;
  header?: ISiteHeader | null;
  listings: ListingCardData[];
  locationName: string;
  locationSlug: string;
  marketSlug?: string;
  nearMeCity?: string | null;
  nearMeCityCount?: number | null;
  nextPage?: number | null;
  pageType: 'city' | 'state';
  seoDescription: string;
  seoTitle: string;
  stateName?: string | null;
  statesWithInventory: StateLink[];
  totalCount: number;
  displayingCity?: string | null;
  availableCities?: Array<{ name: string; slug: string }> | null;
}

export const DirectoryTemplate: React.FC<DirectoryTemplateProps> = (props) => {
  const {
    aiBlurb,
    availableCities,
    canonicalUrl,
    citiesWithInventory,
    currentPage,
    displayingCity,
    footer,
    header,
    listings,
    locationName,
    locationSlug,
    marketSlug,
    nearMeCity,
    nearMeCityCount,
    nextPage,
    pageType,
    seoDescription,
    seoTitle,
    stateName,
    statesWithInventory,
    totalCount,
  } = props;

  const router = useRouter();

  // Use API data directly, only calculate totalPages for display
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showPagination = totalCount > PAGE_SIZE;
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = nextPage != null;

  // Build pagination URL preserving existing query params (like city selection)
  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams();
    // Copy query params except dynamic route segments
    Object.entries(router.query).forEach(([key, value]) => {
      if (key !== 'state' && key !== 'market' && typeof value === 'string') {
        params.set(key, value);
      }
    });
    params.set('page', String(page));
    const basePath = router.asPath.split('?')[0];
    return `${basePath}?${params.toString()}`;
  };

  return (
    <>
      <DirectorySeoHead
        canonicalUrl={canonicalUrl}
        listings={listings}
        locationName={locationName}
        pageType={pageType}
        seoDescription={seoDescription}
        seoTitle={seoTitle}
        stateName={stateName}
        totalCount={totalCount}
      />
      <MarketplaceMapLayout header={header} footer={footer ?? undefined}>
        <Col className="h-full px-2 py-4">
          <Col className="h-full w-full max-w-300 self-center overflow-scroll">
            <DirectoryHeader
              pageType={pageType}
              locationName={locationName}
              locationSlug={locationSlug}
              totalCount={totalCount}
              stateName={stateName}
              displayingCity={displayingCity}
              availableCities={availableCities}
              nearMeCity={nearMeCity}
              nearMeCityCount={nearMeCityCount}
              marketSlug={marketSlug || availableCities?.[0].slug}
            />

            {aiBlurb && (
              <Text as="p" className="text-body-lg pb-2">
                {aiBlurb}
              </Text>
            )}

            <Box className="grid grid-cols-1 gap-6 pb-4 md:grid-cols-2">
              {listings.map((listing, index) => (
                <Box key={`${listing.slug}-${index}`}>
                  <ListingCard listing={listing} />
                </Box>
              ))}
            </Box>

            {showPagination && (
              <Row className="items-center justify-center gap-2 pb-4">
                <Button
                  variant="secondary"
                  aria-label="Go to previous page"
                  analyticsName="directory-previous-page"
                  className="size-7 rounded-full border-od-border-tertiary p-0"
                  {...(hasPreviousPage
                    ? { href: buildPageUrl(currentPage - 1) }
                    : { disabled: true })}
                >
                  <IconChevronLeft size={20} />
                </Button>
                <Text as="span" className="text-body-md">
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  variant="secondary"
                  aria-label="Go to next page"
                  analyticsName="directory-next-page"
                  className="size-7 rounded-full border-od-border-tertiary p-0"
                  {...(hasNextPage ? { href: buildPageUrl(currentPage + 1) } : { disabled: true })}
                >
                  <IconChevronRight size={20} />
                </Button>
              </Row>
            )}

            <Row>
              <CityLinkList cities={citiesWithInventory} />
              <StateLinkList states={statesWithInventory} />
            </Row>
          </Col>
        </Col>
      </MarketplaceMapLayout>
    </>
  );
};
